import * as React from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps } from 'theme/jss-types';
import {
    Col,
    Form,
    FormInstance,
    Select,
    DatePicker,
    Button,
    Alert,
    Spin,
    message,
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import Helper from 'library/Helper';
import { useTranslation } from 'react-i18next';
import { fetchSlotSuggestions, lockSelectedSlot } from 'network/consignments.api';
import { transformPayloadNew } from '../single-cn-new-px';
import { getPiecesDetail, getChildClientFallback } from '../single-consignment-helper';
import { formFields } from 'components/create-consignment/create-modal.constants';
import { useState } from 'react';

const { DeliveryDate, DeliveryTimeSlot } = formFields;

const isDefined = (value: any): boolean => value !== null && value !== undefined && value !== '';

const styles = () => ({
    title: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
        marginLeft: 4,
    },
    container: {
        width: '100%',
        marginBottom: 16,
    },
    slotRow: {
        display: 'flex',
        gap: 8,
    },
    slotSelectWrapper: {
        flex: 1,
    },
    warningAlert: {
        marginTop: 12,
    },
    datePickerStyle: {
        width: '100%',
    },
    loadingOverlay: {
        position: 'relative' as const,
    },
});

interface SlotData {
    label: string;
    value: string;
    vehicle_id: string;
    vehicle_code: string;
    worker_id: string;
    worker_code: string;
    time_window_start: string;
    time_window_end: string;
    slot_duration: number;
}

interface IProps extends StylesProps<ReturnType<typeof styles>> {
    form: FormInstance;
    params: any;
    field: any;
}

const DeliveryDateTimeslotField = (props: IProps) => {
    const {
        form,
        classes,
        params,
        field,
    } = props;

    const {
        getValidationRules,
        getIsRequiredValidation,
        deliveryDateValue,
        onSlotLockSuccess,
        previousSlotData,
        currentSlotData,
        setCurrentSlotData,
        destinationHubTimezone,
        useHubTimezoneForStorage,
        disabledFields = {},
        piecesData,
        manufacturingCountryList,
        customFieldsList,
        isCodFieldPresent,
        showRiskSurchargeType,
        allowChildLoginOnParent,
        destinationHolidays,
        destinationDeliveryDays,
    } = params;
    const isFieldDisabled = !!disabledFields[field?.key]
        || !!disabledFields[DeliveryDate.key]
        || !!disabledFields[DeliveryTimeSlot.key];
    const { t } = useTranslation();

    const [availableSlots, setAvailableSlots] = React.useState<SlotData[]>([]);
    const [isLoadingSlots, setIsLoadingSlots] = React.useState<boolean>(false);
    const [isLockingSlot, setIsLockingSlot] = React.useState<boolean>(false);
    const [isSlotSuccessfullyLocked, setIsSlotSuccessfullyLocked] = useState<boolean>(false);


    const loadSlots = async () => {
        if (!deliveryDateValue) return;

        setIsLoadingSlots(true);

        const formData = { ...form.getFieldsValue(), ...getChildClientFallback(form.getFieldValue('childClient')) };
        formData.piecesDetail = getPiecesDetail(formData, piecesData, manufacturingCountryList);
        const slotFetchPayload = {
            slot_date: moment(deliveryDateValue).format('YYYY-MM-DD').toString(),
            ...transformPayloadNew(formData, {
                isCustomFieldAllowed: true,
                customFieldsList,
                isCodFieldPresent,
                showRiskSurchargeType,
                allowChildLoginOnParent,
            }),
            ...(previousSlotData ? {
                resource_slot_lock_id: previousSlotData.resourceSlotLockId,
            } : {}),
            // time_window_start: '07:00',
            // time_window_end: '13:00',
            // slot_duration: 7200,
        };
        try {
            const response = await fetchSlotSuggestions(slotFetchPayload);
            const slots = response?.data?.slots;
            const consignmentDetails = response?.data?.consignment_details;

            if (consignmentDetails) {
                const originLat = consignmentDetails?.origin_details?.latitude;
                const originLng = consignmentDetails?.origin_details?.longitude;
                const destLat = consignmentDetails?.destination_details?.latitude;
                const destLng = consignmentDetails?.destination_details?.longitude;

                const latLngFields: Record<string, any> = {};
                if (isDefined(originLat) || isDefined(originLng)) {
                    latLngFields.srcAddress = {
                        ...form.getFieldValue('srcAddress'),
                        ...(isDefined(originLat) ? { latitude: String(originLat) } : {}),
                        ...(isDefined(originLng) ? { longitude: String(originLng) } : {}),
                    };
                }
                if (isDefined(destLat) || isDefined(destLng)) {
                    latLngFields.dstAddress = {
                        ...form.getFieldValue('dstAddress'),
                        ...(isDefined(destLat) ? { latitude: String(destLat) } : {}),
                        ...(isDefined(destLng) ? { longitude: String(destLng) } : {}),
                    };
                }

                if (Object.keys(latLngFields).length > 0) {
                    form.setFieldsValue(latLngFields);
                }
            }

            if (Helper.isValidArray(slots)) {
                const transformedSlots: SlotData[] = slots
                    .filter((slot: any) => slot.is_slot_available === true)
                    .map((slot: any) => {
                        const startTime = moment(slot.slot_start_time, 'HH:mm:ss');
                        const endTime = moment(slot.slot_end_time, 'HH:mm:ss');
                        const durationInSeconds = endTime.diff(startTime, 'seconds');

                        return {
                            label: `${slot.slot_start_time} - ${slot.slot_end_time}`,
                            value: `${slot.slot_start_time} - ${slot.slot_end_time}`,
                            vehicle_id: slot.vehicle_id,
                            vehicle_code: slot.vehicle_code,
                            worker_id: slot.worker_id,
                            worker_code: slot.worker_code,
                            time_window_start: slot.slot_start_time.substring(0, 5),
                            time_window_end: slot.slot_end_time.substring(0, 5),
                            slot_duration: durationInSeconds,
                        };
                    });
                setAvailableSlots(transformedSlots);
            } else {
                setAvailableSlots([]);
            }
        } catch (error: any) {
            setAvailableSlots([]);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch available slots';
            message.error(errorMessage);
        } finally {
            setIsLoadingSlots(false);
        }
    };

    React.useEffect(() => {
        loadSlots();
    }, [deliveryDateValue]);

    // Reset lock state when slot is cleared
    React.useEffect(() => {
        const currentSlot = form.getFieldValue(DeliveryTimeSlot.key);
        if (!currentSlot) {
            setIsSlotSuccessfullyLocked(false);
        }
    }, [form.getFieldValue(DeliveryTimeSlot.key)]);

    const handleSlotChange = async (value: string) => {
        setIsLockingSlot(true);

        const timeArray = value.split(' - ');

        const selectedSlotData = availableSlots.find((slot) => slot.value === value);

        try {
            const lockFormData = { ...form.getFieldsValue(), ...getChildClientFallback(form.getFieldValue('childClient')) };
            lockFormData.piecesDetail = getPiecesDetail(lockFormData, piecesData, manufacturingCountryList);
            const lockPayload: any = {
                ...transformPayloadNew(lockFormData, {
                    isCustomFieldAllowed: true,
                    customFieldsList,
                    isCodFieldPresent,
                    showRiskSurchargeType,
                    allowChildLoginOnParent,
                }),
                slot_date: moment(deliveryDateValue).format('YYYY-MM-DD').toString(),
            };

            if (selectedSlotData) {
                lockPayload.vehicle_id = selectedSlotData.vehicle_id;
                lockPayload.vehicle_code = selectedSlotData.vehicle_code;
                lockPayload.worker_id = selectedSlotData.worker_id;
                lockPayload.worker_code = selectedSlotData.worker_code;
                lockPayload.time_window_start = selectedSlotData.time_window_start;
                lockPayload.time_window_end = selectedSlotData.time_window_end;
                lockPayload.slot_duration = selectedSlotData.slot_duration;
            }

            lockPayload.resource_slot_lock_id = currentSlotData?.resourceSlotLockId || previousSlotData?.resourceSlotLockId;

            const response = await lockSelectedSlot(lockPayload);

            if (response?.isSuccess && response?.data) {
                const lockedSlot = response?.data;
                form.setFieldsValue({
                    [DeliveryTimeSlot.key]: [moment(timeArray[0], 'HH:mm:ss'), moment(timeArray[1], 'HH:mm:ss')],
                    resourceSlotLockId: lockedSlot.resource_slot_lock_id,
                });

                setCurrentSlotData({
                    deliveryDate: deliveryDateValue,
                    timeSlot: [moment(timeArray[0], 'HH:mm:ss'), moment(timeArray[1], 'HH:mm:ss')],
                    resourceSlotLockId: lockedSlot.resource_slot_lock_id,
                    slot: Array.isArray(timeArray) && timeArray.length === 2
                        ? `${timeArray[0]} - ${timeArray[1]}`
                        : '',
                });

                message.success('Slot locked successfully');
                setIsSlotSuccessfullyLocked(true);

                onSlotLockSuccess?.();
            } else {
                form.setFieldsValue({
                    [DeliveryTimeSlot.key]: undefined,
                    resourceSlotLockId: undefined,
                });
                setAvailableSlots([]);
                message.error(response?.errorMessage || 'Slot is not available');
            }
        } catch (error: any) {
            form.setFieldsValue({
                [DeliveryTimeSlot.key]: undefined,
                resourceSlotLockId: undefined,
            });
            setAvailableSlots([]);
            const errorMessage = error?.response?.data?.message || error?.message || 'Slot is not available';
            message.error(errorMessage);
        } finally {
            setIsLockingSlot(false);
        }
    };

    const handleFetchLatestSlots = () => {
        form.setFieldsValue({ [DeliveryTimeSlot.key]: undefined });
        setIsSlotSuccessfullyLocked(false);
        loadSlots();
    };

    const handleDisabledDate = (currentDate: any) => {
        if (!currentDate) return false;

        if (currentDate.format('YYYY-MM-DD') < moment().format('YYYY-MM-DD')) {
            return true;
        }

        const dateString = currentDate.format('YYYY-MM-DD');
        if (destinationHolidays && Array.isArray(destinationHolidays)) {
            if (destinationHolidays.includes(dateString)) {
                return true;
            }
        }

        if (destinationDeliveryDays && Array.isArray(destinationDeliveryDays)) {
            const dayName = currentDate.format('ddd').toUpperCase(); // e.g., "MON", "TUE", "WED"
            if (!destinationDeliveryDays.includes(dayName)) {
                return true;
            }
        }

        return false;
    };

    const handleDateChange = () => {
        form.setFieldsValue({
            [DeliveryTimeSlot.key]: undefined,
        });
        setCurrentSlotData(null);
    };

    return (
        <Col span={24} className={classes.container}>
            <Form.Item
                name="resourceSlotLockId"
                hidden
            >
                <input type="hidden" />
            </Form.Item>

            <div className={classes.title}>
                {t(DeliveryDate.key)}
                <span>{getIsRequiredValidation?.(DeliveryDate.key) ? '*' : null}</span>
            </div>
            <Form.Item
                name={DeliveryDate.key}
                key={DeliveryDate.key}
                rules={getValidationRules?.(field)}
            >
                <DatePicker
                    placeholder="Select Date"
                    className={classes.datePickerStyle}
                    disabledDate={handleDisabledDate}
                    format="YYYY-MM-DD"
                    onChange={handleDateChange}
                    disabled={isFieldDisabled}
                />
            </Form.Item>

            {destinationHubTimezone && useHubTimezoneForStorage && (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 12px',
                        marginBottom: 12,
                        border: '1px solid #91caff',
                        borderRadius: 20,
                        background: '#e6f4ff',
                        color: '#0958d9',
                        fontSize: 12,
                        fontFamily: 'Open Sans',
                    }}
                >
                    <ClockCircleOutlined style={{ fontSize: 12 }} />
                    {`All times in ${destinationHubTimezone}`}
                </span>
            )}

            <div className={classes.title}>
                {t('available_slots_for_date')}
            </div>

            <div className={classes.slotRow}>
                <div className={classes.slotSelectWrapper}>
                    <Spin spinning={isLoadingSlots || isLockingSlot}>
                        <Form.Item
                            name={DeliveryTimeSlot.key}
                            key={DeliveryTimeSlot.key}
                            style={{ marginBottom: 0 }}
                            getValueProps={(value) => {
                                if (Array.isArray(value) && value.length === 2) {
                                    const startTime = moment.isMoment(value[0]) ? value[0].format('HH:mm:ss') : value[0];
                                    const endTime = moment.isMoment(value[1]) ? value[1].format('HH:mm:ss') : value[1];
                                    return {
                                        value: `${startTime} - ${endTime}`,
                                    };
                                }
                                return { value };
                            }}
                        >
                            <Select
                                placeholder="Select slot"
                                disabled={
                                    !deliveryDateValue
                                    || isLoadingSlots
                                    || isSlotSuccessfullyLocked
                                    || isFieldDisabled
                                }
                                onChange={handleSlotChange}
                                loading={isLoadingSlots}
                            >
                                {availableSlots.map((slot) => (
                                    <Select.Option key={slot.value} value={slot.value}>
                                        {slot.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Spin>
                </div>

                <Button
                    type="default"
                    onClick={handleFetchLatestSlots}
                    disabled={!deliveryDateValue || isFieldDisabled}
                    loading={isLoadingSlots}
                    danger
                >
                    Fetch Latest Slots
                </Button>
            </div>

            {previousSlotData && (
                <Alert
                    message={t('refetch_slots_required')}
                    description={t('refetch_slots_description', {
                        date: previousSlotData.date,
                        slot: previousSlotData.slot,
                    })}
                    type="warning"
                    showIcon
                    className={classes.warningAlert}
                />
            )}

            {currentSlotData && currentSlotData?.resourceSlotLockId && (
                <Alert
                    message={t('current_slot_locked')}
                    description={t('current_slot_locked_description', {
                        date: currentSlotData.deliveryDate,
                        slot: currentSlotData.slot,
                    })}
                    type="success"
                    showIcon
                    className={classes.warningAlert}
                />
            )}
        </Col>
    );
};

const mapStateToProps = () => {
    return {};
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
};

export default GenericHoc(hocConfig)(DeliveryDateTimeslotField);
